import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { factoryApi, SearchToolResponse, ApiResponse } from '@/lib/api';
import { useMemo } from 'react';

// 查询keys
export const factoryQueryKeys = {
  all: ['factory'] as const,
  inventory: (searchId: string, pageSize?: number, pageNo?: number) => 
    ['factory', 'inventory', { searchId, pageSize, pageNo }] as const,
};

// 使用工厂出入库数据的hook
export function useFactoryInventoryData(
  searchId: string,
  pageSize = 50,
  pageNo = 1,
  enabled = true
): UseQueryResult<ApiResponse<SearchToolResponse>, Error> {
  return useQuery({
    queryKey: factoryQueryKeys.inventory(searchId, pageSize, pageNo),
    queryFn: () => factoryApi.getInventoryData(searchId, pageSize, pageNo),
    enabled: enabled && !!searchId,
    staleTime: 5 * 60 * 1000, // 5分钟
    refetchInterval: 30 * 1000, // 30秒自动刷新
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

// 处理和转换出入库数据的hook
export function useProcessedInventoryData(searchId: string, enabled = true) {
  const { data, isLoading, error, refetch } = useFactoryInventoryData(searchId, 50, 1, enabled);
  
  const processedData = useMemo(() => {
    if (!data?.res_content?.data_list) {
      return {
        inventoryItems: [],
        summary: {
          totalCount: 0,
          inboundCount: 0,
          outboundCount: 0,
          totalAmount: 0,
        },
        charts: {
          typeDistribution: [],
          dailyTrend: [],
        }
      };
    }
    
    const dataList = data.res_content.data_list;
    
    // 转换为库存项目格式 - 按照 API 返回的原始字段顺序
    const inventoryItems = dataList.map((item, index) => {
      // 首先创建一个按照原始数据字段顺序的对象
      const orderedData: any = {};
      
      // 按照原始数据的字段顺序添加字段
      const originalKeys = Object.keys(item);
      originalKeys.forEach(key => {
        orderedData[key] = item[key];
      });
      
      return {
        id: `${item['wdt_spec_no']}-${index}` || `item-${index}`,
        ...orderedData, // 保持原始字段顺序
        // 保留一些计算字段用于KPI显示
        quantity: Number(item['入库数量']) || 0,
        status: getStatusFromType(item['factory_stock_status']),
        inboundDate: formatDate(item['工厂执行日期']),
        expiryDate: calculateExpiryDate(item['工厂执行日期']),
      };
    });
    
    // 生成汇总数据 - 适配新的字段名称
    const summary = {
      totalCount: dataList.length,
      inboundCount: dataList.filter(item => item['tran_type']?.includes('入库')).length,
      outboundCount: dataList.filter(item => item['tran_type']?.includes('出库')).length,
      totalAmount: dataList.reduce((sum, item) => sum + (Number(item['入库数量']) || 0), 0),
    };
    
    // 智能生成图表数据
    const generateChartData = () => {
      // 1. 智能选择分类字段进行统计
      const getDistributionField = () => {
        const priorityFields = ['factory_stock_status', 'tran_type', 'material_category', 'order_type'];
        for (const field of priorityFields) {
          if (dataList.length > 0 && dataList[0][field]) {
            return field;
          }
        }
        return 'factory_stock_status'; // 默认字段
      };
      
      const distributionField = getDistributionField();
      const typeStats = dataList.reduce((acc, item) => {
        const type = item[distributionField] || '未知';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      const typeDistribution = Object.entries(typeStats).map(([name, value]) => ({
        name,
        value,
        percentage: Math.round((value / dataList.length) * 100),
      }));
      
      // 2. 智能选择日期字段进行趋势分析
      const getDateField = () => {
        const dateFields = ['工厂执行日期', '同步到maple时间'];
        for (const field of dateFields) {
          if (dataList.length > 0 && dataList[0][field]) {
            return field;
          }
        }
        return '工厂执行日期'; // 默认字段
      };
      
      // 3. 智能选择数量字段
      const getQuantityField = () => {
        const quantityFields = ['入库数量', '出库数量', '数量'];
        for (const field of quantityFields) {
          if (dataList.length > 0 && dataList[0][field]) {
            return field;
          }
        }
        return '入库数量'; // 默认字段
      };
      
      const dateField = getDateField();
      const quantityField = getQuantityField();
      
      const dateStats = dataList.reduce((acc, item) => {
        const dateValue = item[dateField];
        // 处理日期格式，只取日期部分
        const date = dateValue ? dateValue.split('T')[0] : '未知';
        
        if (!acc[date]) {
          acc[date] = { date, inbound: 0, outbound: 0 };
        }
        
        const quantity = Number(item[quantityField]) || 0;
        
        // 根据事务类型分类
        if (item['tran_type']?.includes('入库')) {
          acc[date].inbound += quantity;
        } else if (item['tran_type']?.includes('出库')) {
          acc[date].outbound += quantity;
        } else {
          // 如果没有明确的事务类型，根据其他字段推断
          acc[date].inbound += quantity;
        }
        
        return acc;
      }, {} as Record<string, { date: string; inbound: number; outbound: number }>);
      
      const dailyTrend = Object.values(dateStats).sort((a, b) => 
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      
      return { typeDistribution, dailyTrend };
    };
    
    const { typeDistribution, dailyTrend } = generateChartData();
    
    return {
      inventoryItems,
      summary,
      charts: {
        typeDistribution,
        dailyTrend,
      }
    };
  }, [data]);
  
  return {
    ...processedData,
    isLoading,
    error,
    refetch,
    totalCount: data?.res_content?.total_count || 0,
    fieldConfig: data?.res_content?.field_conf,
  };
}

// 辅助函数
function getUnitFromType(materialType: string): string {
  switch (materialType) {
    case '原料': return '件';
    case '商品': return '台';
    case '包材': return '个';
    default: return '件';
  }
}

function extractSupplierFromWarehouse(warehouse: string): string {
  if (warehouse?.includes('其然')) return '其然科技';
  if (warehouse?.includes('思贝')) return '思贝集团';
  return '未知供应商';
}

function getStatusFromType(status: string): string {
  if (status === '放行') return '完备';
  if (status === '待检') return '进行中';
  return '待处理';
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '未知';
  // 处理 "2025-06-03T00:00:00" 格式，只取日期部分
  return dateStr.split('T')[0];
}

function calculateExpiryDate(inboundDate: string): string {
  if (!inboundDate) return '未知';
  try {
    const date = new Date(inboundDate.split('T')[0]);
    date.setDate(date.getDate() + 30); // 假设30天有效期
    return date.toISOString().split('T')[0];
  } catch {
    return '未知';
  }
}

// 导出类型
export type ProcessedInventoryItem = {
  id: string;
  batch_no: string;
  brand_name: string;
  factory_stock_status: string;
  material_category: string;
  order_type: string;
  tran_type: string;
  warehouse_name: string;
  wdt_spec_no: string;
  入库数量: number;
  同步到maple时间: string;
  工厂执行日期: string;
  // 保留的计算字段
  quantity: number;
  status: string;
  inboundDate: string;
  expiryDate: string;
}; 